<template>
  <div ref="ctfRichTextContainer" class="contentful-richtext" :class="richTextClasses" :style="richTextStyles">
    <RichTextRenderer :document="content" :node-renderers="renderNodes()" />
  </div>
</template>

<script>
import { h, onMounted,ref, computed, watch, toRefs, reactive } from 'vue';
import RichTextRenderer from '@/components/contentful/ContentfulRichTextRenderer.js';
import { Divider } from '@/pixel/index';
import ContentfulAsset from './ContentfulAsset.vue';
import { getComponent, resolveEntryMutating } from '@/services/ContentfulService.js';
import { getContentfulState } from '@/compositions/useContentful';
import Tracking from '@/components/commons/Tracking.js';
import { UrlUtils } from '@/pixel/index';

export default {
  components: {
    RichTextRenderer,
  },

  props: {
    content: {
      type: Object,
      required: true,
    },
    typographyRule: {
      type: String,
      default: 'Light-Normalcase',
    },
    spacingRule: {
      type: String,
      default: 'Normal',
    },
    align: {
      type: String,
      default: 'left',
    },
    bodyColor: {
      type: String,
      default: undefined,
    },
    headingColor: {
      type: String,
      default: undefined,
    },
    backgroundColor: {
      type: String,
      default: undefined,
    },
    typographyWidthRule: {
      type: String,
      default: 'Normal',
    },
    linkColor: {
      type: String,
      default: undefined,
    },
    linkDecoration: {
      type: String,
      default: 'Inherit',
    },
  },

  async setup(props) {
    const ctfRichTextContainer = ref(null);
    const { linkDecoration, linkColor } = toRefs(props);
    const linkClasses = ref('ctf-link');

    const initializeLinkStyles = () => {
      if (props.linkDecoration === 'Underline') {
        ctfRichTextContainer.value.style.setProperty('--ctf-link-decoration', 'underline');
      } else if (props.linkDecoration === 'None') {
        ctfRichTextContainer.value.style.setProperty('--ctf-link-decoration', 'none');
      }
  
      if (props.linkColor) {
        ctfRichTextContainer.value.style.setProperty('--ctf-link-color', props.linkColor);
      }
    };

    onMounted(() => {
      initializeLinkStyles();
    });

    watch([linkDecoration, linkColor], ([linkDec, linkCol], [prevLinkDec, prevLinkCol]) => {
      if (linkDec !== prevLinkDec || linkCol !== prevLinkCol) {
       initializeLinkStyles();
      }
    })
    
    const richTextClasses = computed(() => {
      const c = [`mw-adjusted-align-${props.align.toLowerCase()}`];
      if (props.spacingRule && props.spacingRule !== 'Inherit') {
        c.push(`spacing-${props.spacingRule.toLowerCase()}`);
      } else {
        c.push(`ctf-spacing-inherit`);
      }

      if (props.typographyRule && props.typographyRule !== 'Inherit') {
        const typography = props.typographyRule.toLowerCase().split('-');
        c.push(`ctf-heading-weight-${typography[0]}`); // light regular semibold
        c.push(`ctf-heading-casing-${typography[1]}`); // normalcase uppercase
      } else {
        c.push('ctf-heading-inherit');
      }

      let typographyMaxWidth = props.typographyWidthRule.toLowerCase(); // width: Narrow ,Normal , Wide ,100% of parent
      if (typographyMaxWidth === '100% of parent') {
        typographyMaxWidth = 'full';
      }

      c.push(`ctf-width-${typographyMaxWidth}`);

      return c;
    });

    const typographyClasses = ref('ctf-heading');
    const typographyBodyClasses = ref('ctf-body');
    const typographyStyles = computed(() => {
      return props.headingColor ? `color:${props.headingColor}` : null;
    });

    const typographyBodyStyles = computed(() => {
      return props.bodyColor ? `color:${props.bodyColor}` : null;
    });

    const richTextStyles = computed(() => {
      if (props.backgroundColor) {
        return `background-color: ${props.backgroundColor};`;
      }
      return '';
    });

    const renderNodes = () => ({
      hr: (node, key, next) => h(Divider, { key, class: 'margin', props: { marginTop: 'var(--space-10)', marginBottom: 'var(--space-10)' } }),
      'heading-1': (node, key, next) =>
        h(
          'h1',
          {
            key,
            class: typographyClasses.value,
            style: typographyStyles.value,
          },
          next(node.content, key, next),
        ),
      'heading-2': (node, key, next) =>
        h(
          'h2',
          {
            key,
            class: typographyClasses.value,
            style: typographyStyles.value,
          },
          next(node.content, key, next),
        ),
      'heading-3': (node, key, next) =>
        h(
          'h3',
          {
            key,
            class: typographyClasses.value,
            style: typographyStyles.value,
          },
          next(node.content, key, next),
        ),
      'heading-4': (node, key, next) =>
        h(
          'h4',
          {
            key,
            class: typographyClasses.value,
            style: typographyStyles.value,
          },
          next(node.content, key, next),
        ),
      'heading-5': (node, key, next) =>
        h(
          'h5',
          {
            key,
            class: typographyClasses.value,
            style: typographyStyles.value,
          },
          next(node.content, key, next),
        ),
      'heading-6': (node, key, next) =>
        h(
          'h6',
          {
            key,
            class: typographyClasses.value,
            style: typographyStyles.value,
          },
          next(node.content, key, next),
        ),
      paragraph: (node, key, next) =>
        h(
          'div',
          {
            key,
            class: [typographyBodyClasses.value, 'paragraph'],
            style: typographyBodyStyles.value,
          },
          next(node.content, key, next),
        ),
      hyperlink: (node, key, next) =>
        h(
          'a',
          {
            key,
            class: linkClasses.value,
            href: node.data.uri,
            target: UrlUtils.isStorefrontUrl(node.data.uri) ? "_self" : "_blank",
          },
          next(node.content, key, next),
        ),
      'embedded-entry-block': function render(node, key, next) {
        const entry = node?.data?.target;
        if (!entry) {
          return h(
            'div',
            {
              class: 'error',
            },
            'This entry cannot be found. Please make sure it is published',
          );
        }
        const component = getComponent(entry.sys?.contentType?.sys?.id);

        if (component) {
          return h(component, { key, ...entry });
        }

        return h('div', {}, `unknown type: ${entry.sys?.contentType?.sys?.id}`);
      },

      // This probably doesn't work very well with most components..
      'embedded-entry-inline': function render(node, key, next) {
        const entry = node?.data?.target;
        const component = getComponent(entry.sys?.contentType?.sys?.id);
        if (component) {
          return h(component, {
            key,
            style: 'display: inline-block;',
            ...entry,
          });
        }
        return h('div', {}, `unknown type: ${entry.sys?.contentType?.sys?.id}`);
      },
      'embedded-asset-block': function render(node, key, next) {
        return h(ContentfulAsset, { key, ...node.data.target });
      },
    });

    try {
      const isPreview = getContentfulState().isPreview;
      await Promise.all(props.content.content.filter((c) => c?.data?.target).map((c) => resolveEntryMutating(c.data?.target, 2, isPreview)));
    } catch (e) {
      Tracking.sentryCaptureError(e);
    }

    return {
      richTextClasses,
      typographyClasses,
      richTextStyles,
      renderNodes,
      ctfRichTextContainer
    };
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

@define-mixin applySpacingRule $vMarmginOut, $vMarginInside {
  padding-top: calc($vMarmginOut/2);
  padding-bottom: calc($vMarmginOut/2);

  .ctf-heading,
  .ctf-body,
  & > .ctf-heading.ctf-heading,
  & > .ctf-body.ctf-body {
    margin-bottom: $vMarginInside;
  }
}
@define-mixin applyTypeRule $fsH1, $fsH2, $fsP {
  /* putting more weight without using !important */
  h1.ctf-heading.ctf-heading {
    font-size: $fsH1;
  }
  h2.ctf-heading.ctf-heading {
    font-size: $fsH2;
  }
  .ctf-body.ctf-body {
    font-size: $fsP;
  }
}

/*! purgecss start ignore */

.contentful-richtext {
  /* #region base fixes */
  li {
    display: list-item;
  }
  ul {
    list-style-type: revert;
    padding: revert;
    border: revert;
    margin: revert;
  }
  p,
  .paragraph {
    min-height: 1em;
  }
  blockquote {
    border-left: 4px solid var(--background-color);
  }
  /* #endregion base fixes */

  /* region GLOBAL TYPOGRAPHY */
  h1.ctf-heading {
    line-height: 1em;

    max-width: 20em;
    &.uppercase {
      max-width: 27em;
    }
  }
  .ctf-heading,
  .ctf-body {
    line-height: 1.25em;

    max-width: 30em;
    &.uppercase {
      max-width: 40.5em;
    }

    min-height: 0;
    /* purgecss ignore */
    &:empty {
      display: none;
    }
  }

  a.ctf-link {
    text-decoration: var(--ctf-link-decoration);
    color: var(--ctf-link-color);
  }

  @mixin applyTypeRule 32px, 32px, 16px;
  /* @media (min-width: $mobile-breakpoint) {
    @mixin applyTypeRule 32px, 32px, 16px;
  }
  @media (min-width: $desktop-breakpoint) {
    @mixin applyTypeRule 32px, 32px, 16px;
  } */
  @media (min-width: $large-breakpoint) {
    @mixin applyTypeRule 48px, 48px, 20px;
  }
  @media (min-width: $xxl-breakpoint) {
    @mixin applyTypeRule 64px, 64px, 32px;
  }

  /* endregion GLOBAL TYPOGRAPHY */

  /* #region SPACING RULES */

  &.spacing-hero,
  &.spacing-hero .ctf-spacing-inherit {
    @mixin applySpacingRule 40px, 24px;

    @media (min-width: $mobile-breakpoint) {
      @mixin applySpacingRule 40px, 40px;

      h2,
      .ctf-body {
        line-height: 1.5em;
      }
    }

    @media (min-width: $desktop-breakpoint) {
      /* @mixin applySpacingRule 40px, 40px  */
    }

    @media (min-width: $large-breakpoint) {
      /* @mixin applySpacingRule 40px, 40px  */
      h2,
      .ctf-body {
        line-height: 1.25em;
      }
    }

    @media (min-width: $xxl-breakpoint) {
      @mixin applySpacingRule 80px, 80px;
    }
  }

  &.spacing-large,
  &.spacing-large .ctf-spacing-inherit {
    @mixin applySpacingRule 40px, 24px;

    @media (min-width: $mobile-breakpoint) {
      @mixin applySpacingRule 40px, 16px;
    }

    /* @media (min-width: $desktop-breakpoint) {
      @mixin applySpacingRule 40px, 16px;
    }
    @media (min-width: $large-breakpoint) {
      @mixin applySpacingRule 40px, 16px;
    } */

    @media (min-width: $xxl-breakpoint) {
      @mixin applySpacingRule 80px, 24px;
    }
  }

  /* #endregion SPACING RULES */

  /* #region TYPOGRAPHY RULES */
  &.ctf-heading-weight-light {
    & > .ctf-heading.ctf-heading,
    .ctf-heading {
      font-family: var(--font-light);
    }
  }
  &.ctf-heading-weight-regular {
    & > .ctf-heading.ctf-heading,
    .ctf-heading {
      font-family: var(--font-regular);
    }
  }
  &.ctf-heading-weight-semibold {
    & > .ctf-heading.ctf-heading,
    .ctf-heading {
      font-family: var(--font-bold);
    }
  }

  &.ctf-heading-casing-normalcase {
    & > .ctf-heading.ctf-heading,
    .ctf-heading {
      text-transform: none;
      letter-spacing: normal;
      max-width: 20em;
    }
    &.ctf-width-wide,
    .ctf-heading-inherit.ctf-width-wide {
      .ctf-heading {
        max-width: 24em;
      }
      .ctf-body {
        max-width: 34em;
      }
    }

    &.ctf-width-normal,
    .ctf-heading-inherit.ctf-width-normal {
      .ctf-heading {
        max-width: 20em;
      }
      .ctf-body {
        max-width: 30em;
      }
    }

    &.ctf-width-narrow,
    .ctf-heading-inherit.ctf-width-narrow {
      .ctf-heading {
        max-width: 20em;
      }
      .ctf-body {
        max-width: 20em;
      }
    }
  }
  &.ctf-heading-casing-uppercase {
    & > .ctf-heading.ctf-heading,
    .ctf-heading {
      text-transform: uppercase;
      letter-spacing: var(--uppercase-letter-spacing);
      max-width: 27em;
    }

    &.ctf-width-normal,
    .ctf-heading-inherit.ctf-width-normal {
      .ctf-heading {
        max-width: calc(20em * 1.5);
      }
      .ctf-body {
        max-width: 30em;
      }
    }

    &.ctf-width-narrow,
    .ctf-heading-inherit.ctf-width-narrow {
      .ctf-heading {
        max-width: calc(20em * 1.5);
      }
      .ctf-body {
        max-width: 20em;
      }
    }

    &.ctf-width-wide,
    .ctf-heading-inherit.ctf-width-wide {
      .ctf-heading {
        max-width: calc(24em * 1.5);
      }
      .ctf-body {
        max-width: 34em;
      }
    }
  }
  &.ctf-width-full {
    & > .ctf-heading.ctf-heading,
    .ctf-heading {
      max-width: 100% !important;
    }
    .ctf-body {
      max-width: 100% !important;
    }
  }
}

/* #region TYPOGRAPHY RULES */

/* #region CONTAINER */

.contentful-container {
  & > div.contentful-richtext {
    & > * {
      /* purgecss ignore */
      &:not(.to-edge) {
        @mixin responsiveContainerPadding;
      }
    }
  }
}

.contentful-container-restricted {
  @media (min-width: $mobile-breakpoint) {
    & > div.contentful-richtext {
      & > * {
        &:not(.bleedout) {
          &,
          &.bleedthrough .ctf-restrict,
          &.bleedthrough .ctf-indent {
            max-width: 700px;
            margin-left: auto;
            margin-right: auto;
          }
        }
      }
    }
    &.wide {
      & > div.contentful-richtext {
        & > * {
          &:not(.bleedout) {
            &,
            &.bleedthrough .ctf-restrict,
            &.bleedthrough .ctf-indent {
              max-width: 2560px;
            }
          }
        }
      }
    }
  }

  &,
  &.wide {
    & > div.contentful-richtext {
      & > * {
        /* purgecss ignore */
        &:not(.bleedout) {
          margin-left: auto;
          margin-right: auto;
        }
        &.bleedout,
        &.bleedthrough {
          max-width: none;
          &.to-edge {
            margin-left: 0;
            margin-right: 0;
          }

          &:not(.to-edge) {
            @mixin responsiveContainerPadding;
          }
        }
        @media (max-width: calc($mobile-breakpoint - 1px)) {
          &:not(.to-edge) {
            @mixin responsiveContainerPadding;
          }
        }
      }
    }
  }
}
/* #endregion CONTAINER */

/*! purgecss end ignore */
</style>
